/**
 * BuildSectionPagesV2 — Phase 9 of the Rebirth migration.
 *
 * Round 5 upgrade: replace the "Phase 9 follow-up" placeholders with
 * first-pass real content for Connectors (3 tabs), Agent Marketplace,
 * Templates. Static representative data so the page never feels empty;
 * swap to real backend reads when those endpoints land.
 */
import React, { useState } from 'react';
import { Cable, Store, FileCode, Plus, ExternalLink, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { isRebirthBuildSectionEnabled } from '../utils/featureFlags';
import ConnectorsPage from './ConnectorsPage';
import GelPlatformConnectorsPage from './GelPlatformConnectorsPage';
import AgentMarketplacePage from './AgentMarketplacePage';
import TemplateCenterPage from './TemplateCenterPage';
import { V2PageShell, Button, Pill, Card, MetricCard, Table } from '../components/v2';

const CONNECTOR_TABS = [
  { id: 'installed',   label: 'Installed' },
  { id: 'marketplace', label: 'Marketplace' },
  { id: 'gel',         label: 'GEL Platform' },
];

/* ═══════════════════════════════════════════════════════════════════
   CONNECTORS — 3 tabs
   ─────────────────────────────────────────────────────────────────── */
function ConnectorsPageV2Inner() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'installed');
  const onTabChange = (id) => {
    setTab(id);
    const params = new URLSearchParams(window.location.search);
    params.set('tab', id);
    window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
  };

  return (
    <V2PageShell
      title="Connectors"
      subtitle="Installed · Marketplace · GEL Platform — 27 + 14 connectors total"
      tags={<Pill tone="cyan" size="sm">Build</Pill>}
      tabs={CONNECTOR_TABS}
      activeTabId={tab}
      onTabChange={onTabChange}
      actions={<Button variant="primary" iconLeft={<Plus />}>+ Connector</Button>}
    >
      {tab === 'installed'   && <InstalledConnectorsTab navigate={navigate} />}
      {tab === 'marketplace' && <MarketplaceConnectorsTab navigate={navigate} />}
      {tab === 'gel'         && <GelConnectorsTab navigate={navigate} />}
    </V2PageShell>
  );
}

function InstalledConnectorsTab({ navigate }) {
  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-4 gap-4">
        <MetricCard label="Installed"        value="27" tone="cyan"    delta={{ value: '+3 this month', direction: 'up', intent: 'positive' }} />
        <MetricCard label="OAuth healthy"    value="24" tone="success" delta={{ value: '3 expiring tokens', direction: 'flat', intent: 'neutral' }} />
        <MetricCard label="Errors / 24h"     value="2"  tone="warning" delta={{ value: 'github + jira', direction: 'flat', intent: 'negative' }} />
        <MetricCard label="Bound to skills"  value="84" tone="trust"   delta={{ value: 'across 22 agents', direction: 'flat', intent: 'neutral' }} />
      </div>
      <Card>
        <Card.Header title="Installed connectors · 27" actions={<Button variant="secondary" size="sm" iconLeft={<ExternalLink />} onClick={() => navigate('/connectors')}>Open full page</Button>} />
        <Card.Body>
          <Table
            ariaLabel="Installed connectors"
            density="compact"
            columns={[
              { key: 'name',     header: 'Connector', render: (r) => <strong className="text-v2-ink">{r.name}</strong> },
              { key: 'category', header: 'Category',  render: (r) => <Pill tone="muted" size="sm">{r.category}</Pill> },
              { key: 'auth',     header: 'Auth',      render: (r) => <Pill tone={r.auth === 'OAuth 2.0' ? 'cyan' : 'trust'} size="sm">{r.auth}</Pill> },
              { key: 'bound',    header: 'Bound',     render: (r) => <span className="text-v2-ink-secondary">{r.bound} agentlets</span> },
              { key: 'health',   header: 'Health',    render: (r) => <Pill tone={r.health === 'healthy' ? 'success' : 'warning'} dot size="sm">{r.health}</Pill> },
              { key: 'last',     header: 'Last call', align: 'right', render: (r) => <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{r.last}</span> },
            ]}
            rows={[
              { name: 'Okta',         category: 'IAM',        auth: 'OAuth 2.0', bound: 4, health: 'healthy', last: '14:08' },
              { name: 'CrowdStrike',  category: 'Threat',     auth: 'API key',   bound: 3, health: 'healthy', last: '14:01' },
              { name: 'Splunk',       category: 'SIEM',       auth: 'API key',   bound: 6, health: 'healthy', last: '13:55' },
              { name: 'ServiceNow',   category: 'ITSM',       auth: 'OAuth 2.0', bound: 5, health: 'healthy', last: '13:52' },
              { name: 'Snyk',         category: 'AppSec',     auth: 'OAuth 2.0', bound: 2, health: 'healthy', last: '13:44' },
              { name: 'GitHub',       category: 'DevOps',     auth: 'OAuth 2.0', bound: 4, health: 'degraded', last: '13:31' },
              { name: 'Jira',         category: 'ITSM',       auth: 'OAuth 2.0', bound: 3, health: 'degraded', last: '13:18' },
              { name: 'Slack',        category: 'Messaging',  auth: 'OAuth 2.0', bound: 7, health: 'healthy', last: '13:08' },
              { name: 'CyberArk',     category: 'PAM',        auth: 'API key',   bound: 2, health: 'healthy', last: '12:54' },
            ]}
            rowKey={(r) => r.name}
          />
        </Card.Body>
      </Card>
    </div>
  );
}

function MarketplaceConnectorsTab() {
  const CATEGORIES = [
    { name: 'IAM', count: 6 }, { name: 'SIEM', count: 8 }, { name: 'ITSM', count: 5 },
    { name: 'AppSec', count: 7 }, { name: 'DevOps', count: 9 }, { name: 'Threat', count: 4 },
    { name: 'PAM', count: 3 }, { name: 'Cloud', count: 12 }, { name: 'Email', count: 4 },
    { name: 'Messaging', count: 5 }, { name: 'Storage', count: 6 }, { name: 'Identity', count: 4 },
    { name: 'Compliance', count: 5 }, { name: 'Endpoint', count: 6 }, { name: 'Network', count: 7 },
    { name: 'Other', count: 9 },
  ];
  return (
    <div className="space-y-5">
      <Card>
        <Card.Header
          title="Browse 100+ connectors across 16 categories"
          actions={
            <div className="flex items-center gap-2 bg-v2-muted rounded-v2-sm px-3 py-1.5">
              <Search size={13} className="text-v2-ink-muted" aria-hidden="true" />
              <input className="bg-transparent border-none outline-none text-[12px] w-48" placeholder="Search connectors…" />
            </div>
          }
        />
        <Card.Body>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {CATEGORIES.map((c) => (
              <div key={c.name} className="border border-v2-border-subtle rounded-v2-sm p-3 hover:border-v2-action transition-colors duration-v2 cursor-pointer">
                <div className="text-[13px] font-semibold text-v2-ink">{c.name}</div>
                <div className="text-[11px] text-v2-ink-muted mt-0.5">{c.count} connectors</div>
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}

function GelConnectorsTab({ navigate }) {
  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-3 gap-4">
        <MetricCard label="GEL connectors"  value="14" tone="trust"   delta={{ value: 'platform-managed', direction: 'flat', intent: 'neutral' }} />
        <MetricCard label="Tenants scoped"  value="12" tone="cyan"    delta={{ value: 'all tenants', direction: 'flat', intent: 'neutral' }} />
        <MetricCard label="Policy bindings" value="38" tone="success" delta={{ value: '0 unbound', direction: 'flat', intent: 'positive' }} />
      </div>
      <Card>
        <Card.Header title="GEL Platform connectors" actions={<Button variant="secondary" size="sm" iconLeft={<ExternalLink />} onClick={() => navigate('/gel-connectors')}>Open full page</Button>} />
        <Card.Body>
          <Table
            ariaLabel="GEL platform connectors"
            density="compact"
            columns={[
              { key: 'name',    header: 'Connector', render: (r) => <strong className="text-v2-ink">{r.name}</strong> },
              { key: 'scope',   header: 'Scope',     render: (r) => <Pill tone="trust" size="sm">{r.scope}</Pill> },
              { key: 'policy',  header: 'Policy bound', render: (r) => <span className="font-mono-v2 text-[11px]">{r.policy}</span> },
              { key: 'tenants', header: 'Tenants',   render: (r) => <span className="tabular-nums">{r.tenants}</span> },
            ]}
            rows={[
              { name: 'gel-anthropic',     scope: 'platform', policy: 'inference-default',     tenants: 12 },
              { name: 'gel-openai',        scope: 'platform', policy: 'inference-fallback',    tenants: 12 },
              { name: 'gel-vault',         scope: 'platform', policy: 'token-vault-default',   tenants: 12 },
              { name: 'gel-audit-store',   scope: 'platform', policy: 'audit-hash-chain',      tenants: 12 },
            ]}
            rowKey={(r) => r.name}
          />
        </Card.Body>
      </Card>
    </div>
  );
}

/* G110 — proper v2 inventory pattern (FacetRail + Table + Slideout). */
import ResolvedConnectorsPageV2 from './ConnectorsPageV2';
export function ResolvedConnectorsPage(props) {
  return <ResolvedConnectorsPageV2 {...props} />;
}

/* G125 — proper v2 inventory pattern. */
import ResolvedGelPlatformConnectorsPageV2 from './GelPlatformConnectorsPageV2';
export function ResolvedGelPlatformConnectorsPage(props) {
  return <ResolvedGelPlatformConnectorsPageV2 {...props} />;
}

/* ═══════════════════════════════════════════════════════════════════
   AGENT MARKETPLACE
   ─────────────────────────────────────────────────────────────────── */
function AgentMarketplacePageV2Inner() {
  const navigate = useNavigate();
  const FEATURED = [
    { name: 'IAM Sentinel',       category: 'IAM',         install: '1,284', rating: 4.8, byline: 'Pre-built Okta + Azure AD + group provisioning skills.' },
    { name: 'Threat Triage Pro',  category: 'SOC',         install: '982',   rating: 4.7, byline: 'CrowdStrike + Splunk + ServiceNow auto-triage workflow.' },
    { name: 'Compliance Auditor', category: 'GRC',         install: '654',   rating: 4.9, byline: 'SOC 2 + HIPAA + PCI evidence collection at scale.' },
    { name: 'AppSec Reviewer',    category: 'AppSec',      install: '481',   rating: 4.6, byline: 'GitHub + Snyk + Jira critical-finding orchestration.' },
    { name: 'IR Orchestrator',    category: 'IR',          install: '372',   rating: 4.7, byline: 'ServiceNow + Slack + EDR incident response playbook.' },
    { name: 'PAM Guardian',       category: 'PAM',         install: '218',   rating: 4.9, byline: 'CyberArk + Vault privileged access certification.' },
  ];
  return (
    <V2PageShell
      title="Agent Marketplace"
      subtitle="Pre-built Agentlets across 6 skill categories"
      tags={<Pill tone="cyan" size="sm">Build</Pill>}
      actions={<Button variant="secondary">Submit Agentlet</Button>}
    >
      <div className="space-y-5">
        <Card>
          <Card.Header title="Featured Agentlets · 6" actions={<Button variant="secondary" size="sm" iconLeft={<ExternalLink />} onClick={() => navigate('/agent-marketplace')}>Open full marketplace</Button>} />
          <Card.Body>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {FEATURED.map((a) => (
                <div key={a.name} className="border border-v2-border-subtle rounded-v2-md p-4 hover:border-v2-action transition-colors duration-v2 flex flex-col">
                  <div className="flex items-center justify-between mb-2">
                    <Pill tone="cyan" size="sm">{a.category}</Pill>
                    <span className="text-[11.5px] text-v2-ink-muted tabular-nums">★ {a.rating} · {a.install}</span>
                  </div>
                  <div className="font-semibold text-v2-ink text-[14px] mb-1">{a.name}</div>
                  <div className="text-[11.5px] text-v2-ink-muted leading-relaxed mb-3 flex-1">{a.byline}</div>
                  <Button variant="primary" size="sm">Install</Button>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      </div>
    </V2PageShell>
  );
}
/* G113 — proper v2 inventory (FacetRail + Table + Slideout). */
import ResolvedAgentMarketplacePageV2 from './AgentMarketplacePageV2';
export function ResolvedAgentMarketplacePage(props) {
  return <ResolvedAgentMarketplacePageV2 {...props} />;
}

/* ═══════════════════════════════════════════════════════════════════
   TEMPLATES
   ─────────────────────────────────────────────────────────────────── */
function TemplateCenterPageV2Inner() {
  const navigate = useNavigate();
  return (
    <V2PageShell
      title="Templates"
      subtitle="Reusable Agent / Workflow templates for rapid deployment"
      tags={<Pill tone="cyan" size="sm">Build</Pill>}
      actions={<Button variant="primary" iconLeft={<Plus />}>+ Template</Button>}
    >
      <div className="space-y-5">
        <div className="grid sm:grid-cols-3 gap-4">
          <MetricCard label="Templates"          value="18" tone="cyan"    delta={{ value: '12 agent · 6 workflow', direction: 'flat', intent: 'neutral' }} />
          <MetricCard label="AI-generated"       value="4"  tone="trust"   delta={{ value: 'this month', direction: 'up', intent: 'positive' }} />
          <MetricCard label="Avg time-to-deploy" value="2.4 min" tone="success" delta={{ value: '−18% vs. manual', direction: 'down', intent: 'positive' }} />
        </div>
        <Card>
          <Card.Header title="Agent templates" actions={<Button variant="secondary" size="sm" iconLeft={<ExternalLink />} onClick={() => navigate('/templates')}>Open full page</Button>} />
          <Card.Body>
            <Table
              ariaLabel="Agent templates"
              density="compact"
              columns={[
                { key: 'name',    header: 'Template', render: (r) => <strong className="text-v2-ink">{r.name}</strong> },
                { key: 'kind',    header: 'Kind',     render: (r) => <Pill tone={r.kind === 'agent' ? 'cyan' : 'trust'} size="sm">{r.kind}</Pill> },
                { key: 'origin',  header: 'Origin',   render: (r) => <Pill tone={r.origin === 'AI' ? 'success' : 'muted'} size="sm">{r.origin}</Pill> },
                { key: 'used',    header: 'Used',     render: (r) => <span className="tabular-nums">{r.used}</span> },
                { key: 'updated', header: 'Updated',  render: (r) => <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{r.updated}</span> },
                { key: 'actions', header: '', align: 'right', render: () => <Button variant="ghost" size="sm">Use</Button> },
              ]}
              rows={[
                { name: 'IAM Provisioning Agent', kind: 'agent',    origin: 'curated', used: 14, updated: 'May 5' },
                { name: 'Threat Triage Workflow', kind: 'workflow', origin: 'curated', used: 22, updated: 'May 7' },
                { name: 'Compliance Evidence Agent', kind: 'agent', origin: 'AI',      used:  6, updated: 'May 8' },
                { name: 'IR Escalation Workflow', kind: 'workflow', origin: 'AI',      used:  4, updated: 'May 8' },
                { name: 'AppSec PR Reviewer',     kind: 'agent',    origin: 'curated', used: 11, updated: 'May 6' },
                { name: 'Access Recertification', kind: 'workflow', origin: 'curated', used: 18, updated: 'May 4' },
              ]}
              rowKey={(r) => r.name}
            />
          </Card.Body>
        </Card>
      </div>
    </V2PageShell>
  );
}
/* G117 — proper v2 inventory pattern. */
import ResolvedTemplateCenterPageV2 from './TemplateCenterPageV2';
export function ResolvedTemplateCenterPage(props) {
  return <ResolvedTemplateCenterPageV2 {...props} />;
}
/* Agentic App Packs — proper v2 inventory pattern. */
import ResolvedAgenticAppPacksPageV2 from './AgenticAppPacksPageV2';
export function ResolvedAgenticAppPacksPage(props) {
  return <ResolvedAgenticAppPacksPageV2 {...props} />;
}
